import React, { useState, useEffect, useCallback, useRef } from 'react';
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-markup';
import 'prismjs/themes/prism-okaidia.css';
import { apiClient } from '../config/api';

interface EmailTemplatesPageProps {
  user: any;
  coachMode?: boolean;
  clientId?: number;
  clientName?: string;
  onNavigate?: (view: string) => void;
}

interface TemplateVariable {
  key: string;
  label: string;
  sampleValue: string;
}

interface TemplateSummary {
  id: number;
  slug: string;
  name: string;
  description: string;
  subject: string;
  variables: TemplateVariable[];
  is_active: boolean;
  updated_by: string | null;
  updated_at: string;
}

interface TemplateFull extends TemplateSummary {
  html_body: string;
  text_body: string | null;
}

export const EmailTemplatesPage: React.FC<EmailTemplatesPageProps> = () => {
  // List state
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Editor state
  const [editing, setEditing] = useState<TemplateFull | null>(null);
  const [editSubject, setEditSubject] = useState('');
  const [editHtml, setEditHtml] = useState('');
  const [editText, setEditText] = useState('');
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  // Test send state
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testMsg, setTestMsg] = useState<string | null>(null);

  // Preview
  const previewRef = useRef<HTMLIFrameElement>(null);

  // Load template list
  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.getEmailTemplates();
      setTemplates(res.templates || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Open editor
  const openEditor = async (slug: string) => {
    try {
      setLoading(true);
      const res = await apiClient.getEmailTemplate(slug);
      const t = res.template;
      setEditing(t);
      setEditSubject(t.subject);
      setEditHtml(t.html_body);
      setEditText(t.text_body || '');
      setSaveMsg(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Live preview — interpolate sample variables client-side
  const getPreviewHtml = useCallback(() => {
    if (!editing) return '';
    let html = editHtml;
    if (editing.variables) {
      for (const v of editing.variables) {
        const regex = new RegExp(`\\{\\{${v.key}\\}\\}`, 'g');
        html = html.replace(regex, v.sampleValue);
      }
    }
    return html;
  }, [editHtml, editing]);

  // Update iframe when preview changes
  useEffect(() => {
    if (previewRef.current) {
      const doc = previewRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(getPreviewHtml());
        doc.close();
      }
    }
  }, [getPreviewHtml]);

  // Save
  const handleSave = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      setSaveMsg(null);
      await apiClient.updateEmailTemplate(editing.slug, {
        subject: editSubject,
        html_body: editHtml,
        text_body: editText || null,
      });
      setSaveMsg('Saved successfully');
      setTimeout(() => setSaveMsg(null), 3000);
    } catch (err: any) {
      setSaveMsg(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Test send
  const handleTestSend = async () => {
    if (!editing || !testEmail) return;
    try {
      setTestSending(true);
      setTestMsg(null);
      const res = await apiClient.testSendEmailTemplate(editing.slug, testEmail);
      setTestMsg(res.message || 'Sent!');
      setTimeout(() => {
        setShowTestModal(false);
        setTestMsg(null);
        setTestEmail('');
      }, 2000);
    } catch (err: any) {
      setTestMsg(`Error: ${err.message}`);
    } finally {
      setTestSending(false);
    }
  };

  // Back to list
  const backToList = () => {
    setEditing(null);
    loadTemplates();
  };

  // ===== RENDER =====

  if (loading && !editing) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-gray-500">Loading email templates...</div>
      </div>
    );
  }

  if (error && !editing) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      </div>
    );
  }

  // ===== EDITOR MODE =====
  if (editing) {
    return (
      <div className="flex flex-col h-full bg-gray-50">
        {/* Header bar */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={backToList} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
              &larr; Back
            </button>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{editing.name}</h2>
              <p className="text-xs text-gray-500">{editing.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {saveMsg && (
              <span className={`text-sm ${saveMsg.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>
                {saveMsg}
              </span>
            )}
            <button
              onClick={() => { setShowTestModal(true); setTestMsg(null); }}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
            >
              Send Test
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Subject line */}
        <div className="bg-white border-b border-gray-200 px-4 py-2 shrink-0">
          <label className="block text-xs font-medium text-gray-500 mb-1">Subject</label>
          <input
            type="text"
            value={editSubject}
            onChange={e => setEditSubject(e.target.value)}
            className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {editing.variables && editing.variables.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {editing.variables.map(v => (
                <span key={v.key} className="text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded border border-amber-200">
                  {`{{${v.key}}}`}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Split editor + preview */}
        <div className="flex-1 flex min-h-0">
          {/* Left: Code editor */}
          <div className="w-1/2 flex flex-col border-r border-gray-200">
            <div className="px-4 py-1.5 bg-gray-800 text-gray-300 text-xs font-medium">HTML Editor</div>
            <div className="flex-1 overflow-auto bg-gray-900">
              <Editor
                value={editHtml}
                onValueChange={setEditHtml}
                highlight={code => Prism.highlight(code, Prism.languages.markup, 'markup')}
                padding={16}
                style={{
                  fontFamily: '"Fira Code", "Fira Mono", monospace',
                  fontSize: 13,
                  lineHeight: 1.5,
                  minHeight: '100%',
                  color: '#f8f8f2',
                  backgroundColor: '#272822',
                }}
              />
            </div>
          </div>

          {/* Right: Live preview */}
          <div className="w-1/2 flex flex-col">
            <div className="px-4 py-1.5 bg-gray-100 text-gray-600 text-xs font-medium border-b border-gray-200">
              Preview (with sample values)
            </div>
            <div className="flex-1 bg-white overflow-auto">
              <iframe
                ref={previewRef}
                title="Email Preview"
                sandbox="allow-same-origin"
                className="w-full h-full border-0"
              />
            </div>
          </div>
        </div>

        {/* Variables reference + text fallback */}
        <div className="bg-white border-t border-gray-200 shrink-0">
          {/* Variables reference */}
          {editing.variables && editing.variables.length > 0 && (
            <div className="px-4 py-2 border-b border-gray-100">
              <div className="text-xs font-medium text-gray-500 mb-1">Available Variables</div>
              <div className="flex flex-wrap gap-3">
                {editing.variables.map(v => (
                  <div key={v.key} className="text-xs">
                    <code className="bg-gray-100 px-1.5 py-0.5 rounded text-blue-700">{`{{${v.key}}}`}</code>
                    <span className="text-gray-500 ml-1">{v.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Collapsible text fallback */}
          <div className="px-4 py-2">
            <button
              onClick={() => setShowTextEditor(!showTextEditor)}
              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <span>{showTextEditor ? '\u25BC' : '\u25B6'}</span>
              Plain Text Fallback
            </button>
            {showTextEditor && (
              <textarea
                value={editText}
                onChange={e => setEditText(e.target.value)}
                rows={6}
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Plain text version (optional — auto-generated from HTML if empty)"
              />
            )}
          </div>
        </div>

        {/* Test Send Modal */}
        {showTestModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-96">
              <h3 className="text-lg font-semibold mb-4">Send Test Email</h3>
              <p className="text-sm text-gray-600 mb-3">
                The email will be sent with sample variable values and a [TEST] prefix on the subject.
              </p>
              <input
                type="email"
                placeholder="recipient@example.com"
                value={testEmail}
                onChange={e => setTestEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm mb-3 focus:outline-none focus:ring-1 focus:ring-blue-500"
                autoFocus
              />
              {testMsg && (
                <p className={`text-sm mb-3 ${testMsg.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>
                  {testMsg}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setShowTestModal(false); setTestMsg(null); setTestEmail(''); }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTestSend}
                  disabled={testSending || !testEmail}
                  className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50"
                >
                  {testSending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===== LIST MODE =====
  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Email Templates</h1>
        <p className="text-sm text-gray-500 mt-1">
          Edit transactional email templates. Changes take effect immediately for all new emails.
        </p>
      </div>

      <div className="space-y-3">
        {templates.map(t => (
          <div
            key={t.slug}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors cursor-pointer"
            onClick={() => openEditor(t.slug)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-base font-semibold text-gray-900">{t.name}</h3>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    t.is_active
                      ? 'bg-green-50 text-green-700 border border-green-200'
                      : 'bg-gray-100 text-gray-500 border border-gray-200'
                  }`}>
                    {t.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mb-2">{t.description}</p>
                <div className="text-xs text-gray-400">
                  <span className="font-medium text-gray-600">Subject:</span>{' '}
                  <span className="font-mono">{t.subject}</span>
                </div>
              </div>
              <div className="text-right shrink-0 ml-4">
                <div className="text-xs text-gray-400">
                  {t.updated_at ? new Date(t.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                </div>
                <button
                  onClick={e => { e.stopPropagation(); openEditor(t.slug); }}
                  className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Edit
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {templates.length === 0 && !loading && (
        <div className="text-center py-12 text-gray-500">
          No email templates found. Run migration 061 to seed the default templates.
        </div>
      )}
    </div>
  );
};

export default EmailTemplatesPage;
